import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import BanfHeader from './banf/BanfHeader';
import BanfItemCard from './banf/BanfItemCard';
import BanfActionButtons from './banf/BanfActionButtons';
import { getReleaseStrategies } from 'core/api/apiService';
import { formatNumberDE } from 'core/utils/format';
import { normalizeKontierungstyp } from 'core/utils/kontierung';

const CreateBanfView = ({ user, onBack, onBanfCreated, onGoToCart, addToCart, showSuccess, showError, editingCartItem, onCartItemUpdated, editingFavorite, onFavoriteUpdated, editingDraft, onDraftUpdated, onDraftsChange, ociItems, onOciItemsProcessed }) => {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false); // Track auto-save status
  // Removed global selectedType - now each item has its own kontierungstyp
  const [expandedItems, setExpandedItems] = useState(new Set()); // Track which items are expanded
  const [activeItemTabs, setActiveItemTabs] = useState({}); // Track active tab for each item
  const [belegart, setBelegart] = useState('NB'); // Default to NB - Bestellanforderung
  const [currentDraftId, setCurrentDraftId] = useState(null); // Track current draft ID for updates
  const [validationErrors, setValidationErrors] = useState({}); // Track validation errors per item: { [itemId]: ['field1', 'field2'] }
  const autoSaveTimeoutRef = useRef(null); // Ref for auto-save debounce timeout
  const isInitializedRef = useRef(false); // Track if component has been initialized
  // Cache for all mapping type lookups: { cost_center: {...}, plant: {...}, purchasing_org: {...}, purchasing_group: {...} }
  const [fieldMappings, setFieldMappings] = useState({
    cost_center: {},
    plant: {},
    purchasing_org: {},
    purchasing_group: {}
  });

  // Freigabestrategien (einmalig beim Laden holen)
  const [releaseStrategies, setReleaseStrategies] = useState([]);

  // Save draft to API
  const saveDraftToApi = useCallback(async (itemsToSave, belegartToSave, draftId = null) => {
    console.log('📝 saveDraftToApi called:', {
      hasToken: !!user?.token,
      itemCount: itemsToSave.length,
      draftId,
      editingCartItem: !!editingCartItem,
      editingFavorite: !!editingFavorite,
      ociItems: !!ociItems
    });

    if (!user?.token || itemsToSave.length === 0) {
      console.log('📝 saveDraftToApi: Early return - no token or no items');
      return null;
    }

    // Don't save drafts in edit modes
    if (editingCartItem || editingFavorite || ociItems) {
      console.log('📝 saveDraftToApi: Early return - in edit mode');
      return null;
    }

    try {
      const draftTitle = itemsToSave[0]?.description
        ? `${itemsToSave[0].description.substring(0, 50)}...`
        : t('banf.draftDate', { date: new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE') });

      const method = draftId ? 'PUT' : 'POST';
      const url = draftId ? `/api/purchase-requests/drafts/${draftId}` : '/api/purchase-requests/drafts';

      console.log('📝 saveDraftToApi: Sending request', { method, url, title: draftTitle });

      // Anhänge im Entwurf nur als METADATEN speichern: der Autosave läuft alle 2 Sekunden —
      // base64-Inhalte (bis 10 MB je Datei) würden bei jedem Tastendruck erneut übertragen
      // und oberhalb des Proxy-Limits still scheitern. Der Inhalt wird erst beim Übergang
      // in den Warenkorb persistiert; beim Entwurf-Laden zeigt der Anhänge-Tab "Inhalt
      // nicht gespeichert — bitte neu anhängen".
      const itemsForDraft = itemsToSave.map((it) => ({
        ...it,
        attachments: Array.isArray(it.attachments)
          ? it.attachments.map(({ data, file, ...meta }) => meta)
          : it.attachments,
      }));

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          items: itemsForDraft,
          belegart: belegartToSave,
          title: draftTitle
        })
      });

      console.log('📝 saveDraftToApi: Response status', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📝 saveDraftToApi: Success', data);
        return data.id || draftId;
      } else {
        const errorText = await response.text();
        console.error('📝 saveDraftToApi: Error response', errorText);
        // Stiller Autosave-Fehler ist Datenverlust — der Nutzer muss es erfahren
        showError?.(t('banf.draftSaveFailed'));
      }
    } catch (error) {
      console.error('📝 Error saving draft to API:', error);
      showError?.(t('banf.draftSaveFailed'));
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, editingCartItem, editingFavorite, ociItems]);

  // Check if items have meaningful content
  const hasContent = useCallback((itemsToCheck) => {
    return itemsToCheck.some(item =>
      item.description?.trim() ||
      item.material?.trim() ||
      item.preferred_vendor?.trim() ||
      item.position_text?.trim() ||
      (item.price && parseFloat(item.price) > 0) ||
      (item.services && item.services.length > 0)
    );
  }, []);

  // Auto-save effect - triggers when items change (with debounce)
  useEffect(() => {
    // Don't auto-save in edit modes or during initial load
    if (editingCartItem || editingFavorite || ociItems || !isInitializedRef.current) {
      return;
    }

    // Clear previous timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (items.length > 0 && hasContent(items)) {
        console.log('📝 Auto-save triggered');
        setAutoSaving(true);
        try {
          const savedDraftId = await saveDraftToApi(items, belegart, currentDraftId);
          if (savedDraftId && !currentDraftId) {
            setCurrentDraftId(savedDraftId);
            console.log('📝 Auto-save: New draft created with ID:', savedDraftId);
            // Badge im Header aktualisieren bei neuem Entwurf
            if (onDraftsChange) {
              onDraftsChange();
            }
          } else if (savedDraftId) {
            console.log('📝 Auto-save: Draft updated:', savedDraftId);
          }
        } catch (error) {
          console.error('📝 Auto-save error:', error);
        } finally {
          setAutoSaving(false);
        }
      }
    }, 2000); // 2 second debounce

    // Cleanup timeout on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [items, belegart, currentDraftId, editingCartItem, editingFavorite, ociItems, hasContent, saveDraftToApi]);

  // Freigabestrategien einmalig beim Laden holen
  useEffect(() => {
    let cancelled = false;
    const fetch_ = async () => {
      try {
        const data = await getReleaseStrategies(user);
        if (!cancelled && Array.isArray(data)) {
          setReleaseStrategies(data);
        }
      } catch (e) {
        // Fehlerfall: Abschnitt einfach nicht rendern (leeres Array bleibt)
        console.warn('Release strategies could not be loaded:', e);
      }
    };
    if (user?.token) {
      fetch_();
    }
    return () => { cancelled = true; };
  }, [user?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle back button - draft should already be saved via auto-save
  const handleBack = useCallback(async () => {
    // Clear any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Final save before leaving (in case there are unsaved changes)
    // Also save when editing a draft (editingDraft is set)
    if (!editingCartItem && !editingFavorite && !ociItems && items.length > 0) {
      if (hasContent(items)) {
        try {
          // Use currentDraftId for both new drafts and editing existing drafts
          const savedDraftId = await saveDraftToApi(items, belegart, currentDraftId);
          console.log('📝 Final save on back:', savedDraftId, 'editingDraft:', !!editingDraft);
        } catch (error) {
          console.error('📝 Error saving draft on back:', error);
        }
      }
    }
    onBack();
  }, [items, belegart, currentDraftId, editingCartItem, editingFavorite, editingDraft, ociItems, hasContent, saveDraftToApi, onBack]);

  // Delete current draft (after successful submission)
  const deleteDraft = useCallback(async (draftId) => {
    if (!draftId || !user?.token) return;

    try {
      await fetch(`/api/purchase-requests/drafts/${draftId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  }, [user?.token]);

  // Fetch mapping name from API for any mapping type
  const fetchMappingName = useCallback(async (mappingType, code) => {
    if (!code || !user?.token) return null;

    // Check cache first
    if (fieldMappings[mappingType]?.[code] !== undefined) {
      return fieldMappings[mappingType][code];
    }

    try {
      const response = await fetch(`/api/admin/mappings/${mappingType}/lookup/${code}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFieldMappings(prev => ({
          ...prev,
          [mappingType]: { ...prev[mappingType], [code]: data.name }
        }));
        return data.name;
      } else {
        // Cache null for not found codes to avoid repeated requests
        setFieldMappings(prev => ({
          ...prev,
          [mappingType]: { ...prev[mappingType], [code]: null }
        }));
        return null;
      }
    } catch (error) {
      console.error(`Error fetching ${mappingType} mapping:`, error);
      return null;
    }
  }, [user?.token, fieldMappings]);

  // Load all unique field values from items for all mapping types
  useEffect(() => {
    const loadAllMappingNames = async () => {
      // Cost centers
      const uniqueCostCenters = [...new Set(items.map(item => item.cost_center).filter(Boolean))];
      for (const code of uniqueCostCenters) {
        if (fieldMappings.cost_center[code] === undefined) {
          await fetchMappingName('cost_center', code);
        }
      }

      // Plants
      const uniquePlants = [...new Set(items.map(item => item.plant).filter(Boolean))];
      for (const code of uniquePlants) {
        if (fieldMappings.plant[code] === undefined) {
          await fetchMappingName('plant', code);
        }
      }

      // Purchasing Orgs
      const uniquePurchasingOrgs = [...new Set(items.map(item => item.einkaueferorganisation).filter(Boolean))];
      for (const code of uniquePurchasingOrgs) {
        if (fieldMappings.purchasing_org[code] === undefined) {
          await fetchMappingName('purchasing_org', code);
        }
      }

      // Purchasing Groups
      const uniquePurchasingGroups = [...new Set(items.map(item => item.einkauefergruppe).filter(Boolean))];
      for (const code of uniquePurchasingGroups) {
        if (fieldMappings.purchasing_group[code] === undefined) {
          await fetchMappingName('purchasing_group', code);
        }
      }
    };

    if (items.length > 0) {
      loadAllMappingNames();
    }
  }, [items, fetchMappingName]);

  // Standard-Kostenstellen für Dropdown
  const costCenters = [
    user?.costCenter || '110700', // Benutzer-Kostenstelle als Standard
    '110700', // IT
    '000000', // Default
    '120000', // Verwaltung
    '130000', // Vertrieb
    '140000', // Produktion
  ].filter((value, index, self) => self.indexOf(value) === index); // Duplikate entfernen

  // Mengeneinheiten aus der SAP-Wertehilfe (in Gebrauch, ST zuerst) statt einer hartkodierten
  // Liste. Genau die alte Liste hat SAP-abgelehnte Codes enthalten (STK/PCE/SET) und einen
  // fehlgeschlagenen Transfer verursacht ("Mengeneinheit STK ist nicht vorgesehen"). Der
  // hartkodierte Fallback bleibt fuer den Fall, dass SAP beim Laden nicht erreichbar ist.
  const FALLBACK_UNITS = ['ST', 'M', 'KG', 'L', 'PAK', 'STD'];
  const [units, setUnits] = useState(FALLBACK_UNITS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/value-help/unit?limit=50', {
          headers: { Authorization: `Bearer ${user?.token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const codes = (data.rows || []).map((r) => r.code).filter(Boolean);
        if (!cancelled && codes.length) setUnits(codes);
      } catch (_e) {
        // Fallback-Liste bleibt aktiv - Erfassung darf an einem SAP-Ausfall nicht scheitern.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token]);

  // Standard-Warengruppen
  const productGroups = [
    t('banf.general.productCategories.it'),
    t('banf.general.productCategories.office'),
    t('banf.general.productCategories.maintenance'),
    t('banf.general.productCategories.cleaning'),
    t('banf.general.productCategories.security'),
    t('banf.general.productCategories.automotive'),
    t('banf.general.productCategories.electronics'),
    t('banf.general.productCategories.other')
  ];

  // Standard-Werke
  const plants = ['1000', '2000', '3000'];

  // Standard-Lieferdatum (heute + 7 Tage)
  const getDefaultDeliveryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  // Neuen Artikel hinzufügen
  const addItem = () => {
    const newItem = {
      id: Date.now(), // Temporäre ID
      position_number: items.length + 1,
      cost_center: user?.costCenter || '110700',
      gl_account: '', // Sachkonto
      material: '',
      description: '',
      quantity: 1,
      unit: 'ST', // SAP-Mengeneinheit (T006/MSEHI); 'STK' aus der alten Liste kennt SAP nicht
      delivery_date: getDefaultDeliveryDate(),
      product_group: '',
      plant: user?.plant || '1000',
      storage_location: '',
      preferred_vendor: '',
      fixed_vendor: '',
      price: '',
      price_unit: 1, // PEINH — Preiseinheit (Preis je X Einheiten); EBAN.PEINH
      currency: 'EUR',
      // Limits fields
      total_limit: '',
      expected_value: '',
      limits_gl_account: '',
      limits_cost_center: '',
      // Leistungen fields - array of services
      services: [],
      // Texte fields
      position_text: '',
      // Anhänge fields - array of attachments
      attachments: [],
      // Additional fields
      anforderer: user?.requestor || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      einkauefergruppe: user?.purchasingGroup || '',
      einkaueferorganisation: user?.purchasingOrg || '',
      // Kontierung fields
      auftrag: '', // Auftrag field for order-based accounting
      ps_psp_pnr: '', // PSP-Element (KNTTP=P); externer Projektschluessel PS_POSID -> EBKN.PS_PSP_PNR
      // Individual type selection per item
      kontierungstyp: 'kostenstelle', // Default to kostenstelle
      // Bezugsquelle fields
      contract: '',
      info_record: '',
      vendor_material_number: ''
    };
    
    const newItems = [...items, newItem];
    setItems(newItems);
    
    // Expand only the new item and collapse all others
    setExpandedItems(new Set([newItem.id]));
    // Set default tab for the new item
    setActiveItemTabs(prev => ({ ...prev, [newItem.id]: 'allgemein' }));
  };

  // Artikel entfernen
  const removeItem = (id) => {
    setItems(items.filter(item => item.id !== id));
    // Remove from expanded items and active tabs
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    setActiveItemTabs(prev => {
      const newTabs = { ...prev };
      delete newTabs[id];
      return newTabs;
    });
    // Positionen neu nummerieren
    setItems(prevItems => 
      prevItems.map((item, index) => ({
        ...item,
        position_number: index + 1
      }))
    );
  };

  // Artikel aktualisieren
  // Beim Kontierungstyp-Wechsel die Objektfelder der ANDEREN Typen leeren (Taras 28.07.):
  // nach Kopieren + F->P blieb 'auftrag' gefuellt — SAP mappte zwar korrekt (nimmt je KNTTP
  // nur das passende Feld), aber Warenkorb/Anzeige zeigten weiter 'Auftrag'.
  const kontierungCleanup = (typ) => ({
    ...(typ !== 'auftrag' ? { auftrag: '' } : {}),
    ...(typ !== 'psp' ? { ps_psp_pnr: '' } : {}),
  });

  const updateItem = (id, field, value) => {
    // Validierungsfehler für dieses Item löschen wenn Feld bearbeitet wird
    if (validationErrors[id]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
    // Bei Änderung des Kontierungstyps prüfen, ob Tab-Wechsel nötig ist
    if (field === 'kontierungstyp') {
      const currentTab = activeItemTabs[id] || 'allgemein';
      const oldItem = items.find(item => item.id === id);
      
      // Wenn von "dienstleistung" zu anderem Typ gewechselt wird und aktueller Tab ist "limits" oder "leistungen"
      if (oldItem && oldItem.kontierungstyp === 'dienstleistung' && 
          value !== 'dienstleistung' && 
          (currentTab === 'limits' || currentTab === 'leistungen')) {
        // Automatisch zu "allgemein" Tab wechseln
        setActiveItemTabs(prev => ({ ...prev, [id]: 'allgemein' }));
      }
      
      // Wenn zu Kostenstelle, Auftrag oder Material gewechselt wird UND Services oder Limits existieren
      if ((value === 'kostenstelle' || value === 'auftrag' || value === 'material') && oldItem) {
        const hasServices = oldItem.services && oldItem.services.length > 0;
        const hasLimits = oldItem.expected_value || oldItem.total_limit;
        
        if (hasServices || hasLimits) {
          // Menge auf 1 setzen und Mengeneinheit auf LE
          setItems(prev => prev.map(item =>
            item.id === id
              ? { ...item,
                  [field]: value,
                  ...(field === 'kontierungstyp' ? kontierungCleanup(value) : {}),
                  quantity: 1,
                  unit: 'LE'
                }
              : item
          ));
          return;
        }
      }
    }
    
    // Prüfe ob Services hinzugefügt werden
    if (field === 'services') {
      const currentItem = items.find(item => item.id === id);
      if (currentItem && (currentItem.kontierungstyp === 'kostenstelle' || 
                          currentItem.kontierungstyp === 'auftrag' || 
                          currentItem.kontierungstyp === 'material')) {
        // Wenn Services hinzugefügt werden (Array nicht leer)
        if (value && value.length > 0) {
          setItems(prev => prev.map(item =>
            item.id === id
              ? { ...item,
                  [field]: value,
                  ...(field === 'kontierungstyp' ? kontierungCleanup(value) : {}),
                  quantity: 1,
                  unit: 'LE'
                }
              : item
          ));
          return;
        }
      }
    }
    
    // Prüfe ob Limits-Felder gesetzt werden
    if ((field === 'expected_value' || field === 'total_limit') && value) {
      const currentItem = items.find(item => item.id === id);
      if (currentItem && (currentItem.kontierungstyp === 'kostenstelle' || 
                          currentItem.kontierungstyp === 'auftrag' || 
                          currentItem.kontierungstyp === 'material')) {
        setItems(prev => prev.map(item =>
          item.id === id
            ? { ...item,
                [field]: value,
                quantity: 1,
                unit: 'LE'
              }
            : item
        ));
        return;
      }
    }

    // Funktionales Update: mehrere updateItem-Aufrufe im selben Tick (z. B. Material-Auswahl
    // fuellt Kurztext + Einheit + Warengruppe) duerfen sich nicht gegenseitig ueberschreiben.
    setItems(prev => prev.map(item =>
      item.id === id
        ? { ...item, [field]: value, ...(field === 'kontierungstyp' ? kontierungCleanup(value) : {}) }
        : item
    ));
  };

  // Artikel kopieren
  // BANF-Position als Favorit speichern (POST) — analog zum Warenkorb-Stern
  const handleAddItemToFavorites = async (item) => {
    if (!user?.token) {
      showError?.(t('cart.loginForFavorites'));
      return;
    }
    if (!item?.description || !item.description.trim()) {
      showError?.(t('banf.val.shortTextRequired'));
      return;
    }
    try {
      const favorite = {
        name: item.description,
        description: item.position_text || '',
        quantity: parseFloat(item.quantity) || 1,
        unit: item.unit || 'ST',
        price: parseFloat(item.price || 0),
        currency: item.currency || 'EUR',
        supplier: item.preferred_vendor || '',
        supplier_id: null,
        material_number: item.material || '',
        product_group: item.product_group || '',
        cost_center: item.cost_center || '',
        plant: item.plant || '',
        storage_location: item.storage_location || '',
        gl_account: item.gl_account || '',
        order_number: item.auftrag || '',
        wbs_element: '',
        asset_number: '',
        network_number: '',
        preferred_vendor: item.preferred_vendor || '',
        manufacturer: item.manufacturer || '',
        manufacturer_part_number: item.manufacturer_part_number || '',
        einkauefergruppe: item.einkauefergruppe || '',
        einkaueferorganisation: item.einkaueferorganisation || '',
        anforderer: item.anforderer || '',
        kontierungstyp: normalizeKontierungstyp(item.kontierungstyp),
        item_data: item
      };
      const response = await fetch('/api/favorites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
        body: JSON.stringify(favorite)
      });
      if (response.ok) {
        showSuccess?.(t('cart.addedToFavorites'));
      } else {
        showError?.(t('cart.errorSavingFavorite'));
      }
    } catch (error) {
      console.error('Error saving favorite from BANF:', error);
      showError?.(t('cart.errorSavingFavorite'));
    }
  };

  const copyItem = (id) => {
    const itemToCopy = items.find(item => item.id === id);
    if (!itemToCopy) return;

    // Vollständige Position kopieren
    const copiedItem = {
      ...itemToCopy,
      id: Date.now(), // Neue ID für das kopierte Item
      position_number: items.length + 1,
      // Kopiere Services mit neuen IDs falls vorhanden
      services: itemToCopy.services ? itemToCopy.services.map(service => ({
        ...service,
        id: Date.now() + Math.random() * 1000 // Neue Service-ID
      })) : [],
      // Kopiere Bezugsquelle fields
      contract: itemToCopy.contract || '',
      info_record: itemToCopy.info_record || '',
      vendor_material_number: itemToCopy.vendor_material_number || '',
      // Kopiere Lagerort field
      storage_location: itemToCopy.storage_location || ''
    };

    const newItems = [...items, copiedItem];
    setItems(newItems);

    // Expand only the new copied item and collapse all others
    setExpandedItems(new Set([copiedItem.id]));
    // Set default tab for the copied item
    setActiveItemTabs(prev => ({ ...prev, [copiedItem.id]: 'allgemein' }));

    showSuccess?.(t('banf.positionCopied', { position: itemToCopy.position_number }));
  };

  // Gesamtsumme berechnen (reaktiv mit useMemo) - berücksichtigt expected_value, Services und price
  const totalAmount = useMemo(() => {
    return (Array.isArray(items) ? items : []).reduce((sum, item) => {
      // First check if expected_value is set
      if (item.expected_value && parseFloat(item.expected_value) > 0) {
        const expectedValue = parseFloat(item.expected_value) || 0;
        // Add services total if they exist
        if (Array.isArray(item.services) && item.services.length > 0) {
          const servicesTotal = item.services.reduce((serviceSum, service) => {
            const quantity = parseFloat(service.service_quantity) || 0;
            const price = parseFloat(service.service_gross_price) || 0;
            return serviceSum + (quantity * price);
          }, 0);
          return sum + expectedValue + servicesTotal;
        }
        // If expected_value but no services, multiply by quantity
        const quantity = parseFloat(item.quantity) || 1;
        return sum + (expectedValue * quantity);
      }
      // If no expected_value but services exist
      else if (Array.isArray(item.services) && item.services.length > 0) {
        // Calculate services total directly
        const servicesTotal = item.services.reduce((serviceSum, service) => {
          const quantity = parseFloat(service.service_quantity) || 0;
          const price = parseFloat(service.service_gross_price) || 0;
          return serviceSum + (quantity * price);
        }, 0);
        return sum + servicesTotal;
      } else {
        // For regular items without services or expected_value, multiply by quantity
        return sum + (parseFloat(item.price || 0) * parseFloat(item.quantity || 0));
      }
    }, 0);
  }, [JSON.stringify((Array.isArray(items) ? items : []).map(item => ({
    price: item.price,
    quantity: item.quantity,
    expected_value: item.expected_value,
    servicesCount: Array.isArray(item.services) ? item.services.length : 0,
    services: (Array.isArray(item.services) ? item.services : []).map(s => ({
      quantity: s.service_quantity,
      price: s.service_gross_price
    }))
  })))]);

  // Fehlende Pflichtfelder für ein Item ermitteln
  const getMissingFields = useCallback((item) => {
    const missing = [];

    // Grundlegende Pflichtfelder
    if (!item.description || !item.description.trim()) {
      missing.push(t('banf.valFieldShortText'));
    }
    if (!item.quantity || parseFloat(item.quantity) <= 0) {
      missing.push(t('banf.valFieldQuantity'));
    }
    if (!item.delivery_date) {
      missing.push(t('banf.valFieldDeliveryDate'));
    }
    if (!item.product_group || !item.product_group.trim()) {
      missing.push(t('banf.valFieldProductGroup'));
    }

    // Preis-Validierung
    const effectivePrice = item.expected_value ||
      (item.services && item.services.length > 0
        ? item.services.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0)
        : parseFloat(item.price || 0));

    if (!effectivePrice || effectivePrice <= 0) {
      missing.push(t('banf.valFieldPrice'));
    }

    // Validierung basierend auf Kontierungstyp
    if (item.kontierungstyp === 'auftrag') {
      if (!item.auftrag || !item.auftrag.trim()) {
        missing.push(t('banf.valFieldOrder'));
      }
      if (!item.cost_center || !item.cost_center.trim()) {
        missing.push(t('banf.valFieldCostCenter'));
      }
    } else if (item.kontierungstyp === 'kostenstelle') {
      if (!item.cost_center || !item.cost_center.trim()) {
        missing.push(t('banf.valFieldCostCenter'));
      }
      if (!item.gl_account || !item.gl_account.trim()) {
        missing.push(t('banf.valFieldGlAccount'));
      }
    } else if (item.kontierungstyp === 'psp') {
      // SAP-Pflicht bei KNTTP P: ohne PS_PSP_PNR lehnt BAPI_PR_CREATE die Position ab
      if (!item.ps_psp_pnr || !item.ps_psp_pnr.trim()) {
        missing.push(t('banf.valFieldPsp'));
      }
      if (!item.gl_account || !item.gl_account.trim()) {
        missing.push(t('banf.valFieldGlAccount'));
      }
    } else if (item.kontierungstyp !== 'material') {
      if (!item.gl_account || !item.gl_account.trim()) {
        missing.push(t('banf.valFieldGlAccount'));
      }
    }

    return missing;
  }, []);

  // BANF-Artikel zum Warenkorb hinzufügen
  const addBanfToCart = async () => {
    if (items.length === 0) {
      showError?.(t('banf.addItemMin'));
      return;
    }

    // Validierung für Pflichtfelder - fehlende Felder pro Item sammeln
    const errors = {};
    items.forEach(item => {
      const missing = getMissingFields(item);
      if (missing.length > 0) {
        errors[item.id] = missing;
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);

      // Detaillierte Fehlermeldung mit Position und fehlenden Feldern
      const errorParts = Object.entries(errors).map(([itemId, fields]) => {
        const item = items.find(i => i.id === parseInt(itemId) || i.id === itemId);
        const posNr = item ? item.position_number : '?';
        return t('banf.positionError', { n: posNr, fields: fields.join(', ') });
      });

      if (errorParts.length === 1) {
        showError?.(t('banf.val.missingFields', { parts: errorParts[0] }));
      } else {
        showError?.(t('banf.val.missingFieldsMulti', { parts: errorParts.join('\n') }));
      }

      // Erste fehlerhafte Position aufklappen
      const firstErrorItemId = Object.keys(errors)[0];
      const parsedId = parseInt(firstErrorItemId) || firstErrorItemId;
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        newSet.add(parsedId);
        return newSet;
      });

      return;
    }

    // Validierung bestanden - Fehler zurücksetzen
    setValidationErrors({});

    setSaving(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      // Jeden BANF-Artikel einzeln zum Warenkorb hinzufügen
      for (const item of items) {
        try {
          // BANF-Artikel in Warenkorb-Format konvertieren
          const cartItem = {
            description: item.description,
            quantity: parseFloat(item.quantity),
            price: parseFloat(item.price || 0),
            price_unit: parseInt(item.price_unit, 10) || 1, // PEINH -> EBAN.PEINH; Wert = menge*preis/peinh
            currency: item.currency || 'EUR',
            unit: item.unit || 'STK',
            image_url: item.image_url || '', // OCI-Produktbild in die Warenkorb-Position übernehmen
            vendormat: item.material || '',
            vendor: item.preferred_vendor || '',
            shop_id: null, // BANF-Artikel gehören zu keinem spezifischen Shop
            // BANF-spezifische Felder als zusätzliche Eigenschaften
            kostenstelle: item.cost_center,
            sachkonto: item.gl_account,
            delivery_date: item.delivery_date,
            product_group: item.product_group,
            plant: item.plant,
            storage_location: item.storage_location, // Lagerort hinzufügen
            kontierungstyp: item.kontierungstyp,
            anforderer: user?.requestor || `${user.firstName} ${user.lastName}`,
            einkauefergruppe: user?.purchasingGroup || '',
            einkaueferorganisation: user?.purchasingOrg || '',
            // Zusätzliche Felder für vollständige Datenübertragung
            cost_center: item.cost_center,
            gl_account: item.gl_account, // Sachkonto als separates Feld
            material: item.material, // Material als separates Feld
            matnr: item.material, // Material-Nr als zusätzliches Feld
            preferred_vendor: item.preferred_vendor,
            fixed_vendor: item.fixed_vendor,
            auftrag: item.auftrag,
            ps_psp_pnr: item.ps_psp_pnr, // PSP-Element (KNTTP=P) -> items_json -> Mapper -> EBKN.PS_PSP_PNR
            total_limit: item.total_limit,
            expected_value: item.expected_value,
            limits_gl_account: item.limits_gl_account,
            limits_cost_center: item.limits_cost_center,
            services: item.services,
            position_text: item.position_text,
            header_text: item.header_text, // Kopftext (Texte-Tab) -> cart_items.header_text -> SAP Kopfnotiz (§7.4 P17)
            attachments: item.attachments,
            contract: item.contract,
            info_record: item.info_record,
            vendor_material_number: item.vendor_material_number,
            belegart: belegart,
            requesting_department: item.requesting_department,
            requesting_cost_center: item.requesting_cost_center,
            material_group: item.material_group,
            longtext: t('banf.longtextTemplate', { pos: item.position_number, date: item.delivery_date }) + (item.product_group ? t('banf.longtextGroup', { value: item.product_group }) : '') + (item.plant ? t('banf.longtextPlant', { value: item.plant }) : '') + (item.storage_location ? t('banf.longtextStorage', { value: item.storage_location }) : '')
          };

          const response = await fetch('/api/cart', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${user.token}`
            },
            body: JSON.stringify(cartItem)
          });

          if (response.ok) {
            successCount++;
          } else {
            const errorData = await response.json();
            console.error('Fehler beim Hinzufügen von Artikel:', item.description, errorData);
            errorCount++;
          }
        } catch (error) {
          console.error('Fehler beim Hinzufügen von Artikel:', item.description, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        showSuccess?.(t('banf.cartSuccess', { count: successCount }));

        // Clear auto-save timeout to prevent saving after deletion
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
          autoSaveTimeoutRef.current = null;
        }

        // Disable auto-save
        isInitializedRef.current = false;

        setItems([]); // Liste leeren
        setExpandedItems(new Set()); // Expanded items zurücksetzen
        setActiveItemTabs({}); // Active tabs zurücksetzen

        // Entwurf aus der Datenbank löschen
        if (currentDraftId) {
          console.log('📝 Deleting draft after successful cart add:', currentDraftId);
          deleteDraft(currentDraftId);
          setCurrentDraftId(null);
          // Badge im Header aktualisieren
          if (onDraftsChange) {
            onDraftsChange();
          }
        }

        // Optional: Warenkorb neu laden (falls addToCart-Funktion verfügbar)
        if (window.reloadCart) {
          window.reloadCart();
        }

        // Wenn OCI-Items verarbeitet wurden, spezielle Callback-Funktion aufrufen
        if (ociItems && onOciItemsProcessed) {
          setTimeout(() => {
            onOciItemsProcessed();
          }, 1000);
        } else if (editingDraft && onDraftUpdated) {
          // Wenn ein Entwurf bearbeitet wurde, zurück zur Entwürfe-Liste
          setTimeout(() => {
            onDraftUpdated();
          }, 1000);
        } else if (onGoToCart) {
          // Automatisch zum Warenkorb weiterleiten
          setTimeout(() => {
            onGoToCart();
          }, 1000); // 1 Sekunde warten, damit die Success-Meldung sichtbar bleibt
        }
      }

      if (errorCount > 0) {
        showError?.(t('banf.cartPartialError', { count: errorCount }));
      }

    } catch (error) {
      console.error('Error adding BANF to cart:', error);
      showError?.(t('banf.cartError') + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Update cart item when in edit mode
  const updateCartItem = async () => {
    if (items.length === 0) {
      showError?.(t('banf.updateNotFound'));
      return;
    }

    const item = items[0]; // First item is the one being edited
    const additionalItems = items.slice(1); // Additional items to add as new cart entries

    // Validierung für Pflichtfelder - gleiche Regeln wie bei addBanfToCart
    // Grundlegende Pflichtfelder
    if (!item.description || !item.description.trim()) {
      showError?.(t('banf.val.shortTextRequired'));
      return;
    }

    if (!item.quantity || parseFloat(item.quantity) <= 0) {
      showError?.(t('banf.val.quantityRequired'));
      return;
    }

    if (!item.delivery_date) {
      showError?.(t('banf.val.deliveryDateRequired'));
      return;
    }

    // Preis-Validierung (Geschätzter Preis ist Pflichtfeld)
    const effectivePrice = item.expected_value ||
      (item.services && item.services.length > 0
        ? item.services.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0)
        : parseFloat(item.price || 0));

    if (!effectivePrice || effectivePrice <= 0) {
      showError?.(t('banf.val.priceRequired'));
      return;
    }

    // Validierung basierend auf Kontierungstyp
    if (item.kontierungstyp === 'auftrag') {
      // Bei Auftrag-Typ muss Auftrag ausgefüllt sein
      if (!item.auftrag || !item.auftrag.trim()) {
        showError?.(t('banf.val.orderRequired'));
        return;
      }
      // Kostenstelle ist bei Auftrag auch Pflichtfeld
      if (!item.cost_center || !item.cost_center.trim()) {
        showError?.(t('banf.val.costCenterOrderRequired'));
        return;
      }
    } else if (item.kontierungstyp === 'kostenstelle') {
      // Bei Kostenstelle-Typ müssen Kostenstelle und Sachkonto ausgefüllt sein
      if (!item.cost_center || !item.cost_center.trim()) {
        showError?.(t('banf.val.costCenterRequired'));
        return;
      }
      if (!item.gl_account || !item.gl_account.trim()) {
        showError?.(t('banf.val.glAccountCostCenter'));
        return;
      }
    } else if (item.kontierungstyp === 'psp') {
      // SAP-Pflicht bei KNTTP P: ohne PSP-Element lehnt BAPI_PR_CREATE die Position ab
      if (!item.ps_psp_pnr || !item.ps_psp_pnr.trim()) {
        showError?.(t('banf.val.pspRequired'));
        return;
      }
      if (!item.gl_account || !item.gl_account.trim()) {
        showError?.(t('banf.val.glAccountRequired'));
        return;
      }
    } else if (item.kontierungstyp !== 'material') {
      // Für andere Typen außer Material muss Sachkonto ausgefüllt sein
      if (!item.gl_account || !item.gl_account.trim()) {
        showError?.(t('banf.val.glAccountRequired'));
        return;
      }
    }

    // Validate additional items (if any) - basic validation
    for (let i = 0; i < additionalItems.length; i++) {
      const addItem = additionalItems[i];
      const posNum = i + 2; // Position number (2, 3, 4, ...)

      if (!addItem.description?.trim()) {
        showError?.(t('banf.val.posShortText', { pos: posNum }));
        return;
      }
      if (!addItem.delivery_date) {
        showError?.(t('banf.val.posDeliveryDate', { pos: posNum }));
        return;
      }

      // Price validation for additional items
      const addEffectivePrice = addItem.expected_value ||
        (addItem.services?.length > 0
          ? addItem.services.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0)
          : parseFloat(addItem.price || 0));
      if (!addEffectivePrice || addEffectivePrice <= 0) {
        showError?.(t('banf.val.posPrice', { pos: posNum }));
        return;
      }
    }

    setSaving(true);
    try {
      // Prepare cart item data for update
      const updateData = {
        description: item.description,
        quantity: parseFloat(item.quantity),
        price: parseFloat(item.price || 0),
        price_unit: parseInt(item.price_unit, 10) || 1, // PEINH auch im Bearbeiten-Pfad
        currency: item.currency || 'EUR',
        unit: item.unit || 'STK',
        vendormat: item.material || '',
        vendor: item.preferred_vendor || '',
        // Map all CreateBanfView fields to cart item fields
        cost_center: item.cost_center,
        kostenstelle: item.cost_center,
        gl_account: item.gl_account,
        sachkonto: item.gl_account,
        delivery_date: item.delivery_date,
        product_group: item.product_group,
        plant: item.plant,
        storage_location: item.storage_location,
        preferred_vendor: item.preferred_vendor,
        fixed_vendor: item.fixed_vendor,
        total_limit: item.total_limit,
        expected_value: item.expected_value,
        limits_gl_account: item.limits_gl_account,
        limits_cost_center: item.limits_cost_center,
        services: item.services,
        position_text: item.position_text,
        header_text: item.header_text, // Kopftext auch im Bearbeiten-Pfad (Reviewer 28.07.)
        attachments: item.attachments,
        anforderer: item.anforderer,
        einkauefergruppe: item.einkauefergruppe,
        einkaueferorganisation: item.einkaueferorganisation,
        auftrag: item.auftrag,
        ps_psp_pnr: item.ps_psp_pnr,
        kontierungstyp: item.kontierungstyp,
        contract: item.contract,
        info_record: item.info_record,
        vendor_material_number: item.vendor_material_number,
        belegart: belegart,
        requesting_department: item.requesting_department,
        requesting_cost_center: item.requesting_cost_center,
        material_group: item.material_group,
        material: item.material,
        matnr: item.material,
        longtext: item.position_text
      };

      const response = await fetch(`/api/cart/items/${editingCartItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        // If there are additional items, add them to cart as new entries
        let additionalSuccessCount = 0;
        let additionalErrorCount = 0;

        for (const additionalItem of additionalItems) {
          try {
            // Validate additional item
            if (!additionalItem.description?.trim() || !additionalItem.delivery_date) {
              additionalErrorCount++;
              continue;
            }

            const cartItem = {
              description: additionalItem.description,
              quantity: parseFloat(additionalItem.quantity) || 1,
              price: parseFloat(additionalItem.price || 0),
              currency: additionalItem.currency || 'EUR',
              unit: additionalItem.unit || 'STK',
              vendormat: additionalItem.material || '',
              vendor: additionalItem.preferred_vendor || '',
              shop_id: null,
              kostenstelle: additionalItem.cost_center,
              sachkonto: additionalItem.gl_account,
              delivery_date: additionalItem.delivery_date,
              product_group: additionalItem.product_group,
              plant: additionalItem.plant,
              storage_location: additionalItem.storage_location,
              kontierungstyp: additionalItem.kontierungstyp,
              anforderer: additionalItem.anforderer || user?.requestor || `${user.firstName} ${user.lastName}`,
              einkauefergruppe: additionalItem.einkauefergruppe || user?.purchasingGroup || '',
              einkaueferorganisation: additionalItem.einkaueferorganisation || user?.purchasingOrg || '',
              cost_center: additionalItem.cost_center,
              gl_account: additionalItem.gl_account,
              material: additionalItem.material,
              matnr: additionalItem.material,
              preferred_vendor: additionalItem.preferred_vendor,
              fixed_vendor: additionalItem.fixed_vendor,
              auftrag: additionalItem.auftrag,
              total_limit: additionalItem.total_limit,
              expected_value: additionalItem.expected_value,
              limits_gl_account: additionalItem.limits_gl_account,
              limits_cost_center: additionalItem.limits_cost_center,
              services: additionalItem.services,
              position_text: additionalItem.position_text,
              header_text: additionalItem.header_text, // Kopftext auch für Zusatzpositionen
              price_unit: parseInt(additionalItem.price_unit, 10) || 1,
              attachments: additionalItem.attachments,
              contract: additionalItem.contract,
              info_record: additionalItem.info_record,
              vendor_material_number: additionalItem.vendor_material_number,
              belegart: belegart,
              requesting_department: additionalItem.requesting_department,
              requesting_cost_center: additionalItem.requesting_cost_center,
              material_group: additionalItem.material_group,
              longtext: additionalItem.position_text
            };

            const addResponse = await fetch('/api/cart', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.token}`
              },
              body: JSON.stringify(cartItem)
            });

            if (addResponse.ok) {
              additionalSuccessCount++;
            } else {
              additionalErrorCount++;
            }
          } catch (err) {
            console.error('Error adding additional item:', err);
            additionalErrorCount++;
          }
        }

        // Show appropriate success message
        if (additionalItems.length === 0) {
          showSuccess?.(t('banf.updateSuccess'));
        } else if (additionalSuccessCount > 0 && additionalErrorCount === 0) {
          showSuccess?.(t('banf.updateAddedSuccess', { count: additionalSuccessCount }));
        } else if (additionalSuccessCount > 0) {
          showSuccess?.(t('banf.updatePartialError', { success: additionalSuccessCount, error: additionalErrorCount }));
        } else {
          showSuccess?.(t('banf.updateAddError'));
        }

        // Call the callback to notify parent component
        if (onCartItemUpdated) {
          onCartItemUpdated();
        }
      } else {
        const errorData = await response.json();
        console.error('Fehler beim Aktualisieren des Artikels:', errorData);
        
        if (response.status === 401 || response.status === 403) {
          showError?.(t('banf.sessionExpired', { error: errorData.error || t('banf.sessionExpiredDefault') }));
          localStorage.removeItem('sap_oci_user');
          window.location.reload();
          return;
        }

        showError?.(t('banf.updateError') + (errorData.error || t('banf.unknownError')));
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
      showError?.(t('banf.updateError') + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Update favorite function
  const updateFavorite = async () => {
    if (items.length === 0) {
      showError?.(t('banf.updateNotFound'));
      return;
    }

    if (items.length > 1) {
      showError?.(t('banf.editSingleOnly'));
      return;
    }

    const item = items[0]; // Only one item should exist in edit mode

    // Validation for required fields
    if (!item.description.trim()) {
      showError?.(t('banf.val.shortTextRequired'));
      return;
    }

    setSaving(true);
    try {
      // Prepare favorite data for update
      const updateData = {
        name: item.description,
        description: item.position_text || '',
        quantity: parseFloat(item.quantity) || 1,
        unit: item.unit || 'ST',
        price: parseFloat(item.price || 0),
        currency: item.currency || 'EUR',
        supplier: item.preferred_vendor || '',
        supplier_id: null,
        material_number: item.material || '',
        product_group: item.product_group || '',
        cost_center: item.cost_center || '',
        plant: item.plant || '',
        storage_location: item.storage_location || '',
        gl_account: item.gl_account || '',
        order_number: item.auftrag || '',
        wbs_element: '',
        asset_number: '',
        network_number: '',
        preferred_vendor: item.preferred_vendor || '',
        fixed_vendor: item.fixed_vendor || '',
        manufacturer: '',
        manufacturer_part_number: '',
        einkauefergruppe: item.einkauefergruppe || '',
        einkaueferorganisation: item.einkaueferorganisation || '',
        anforderer: item.anforderer || '',
        kontierungstyp: normalizeKontierungstyp(item.kontierungstyp),
        item_data: {
          ...item,
          services: item.services,
          attachments: item.attachments,
          contract: item.contract,
          info_record: item.info_record,
          vendor_material_number: item.vendor_material_number,
          total_limit: item.total_limit,
          expected_value: item.expected_value,
          limits_gl_account: item.limits_gl_account,
          limits_cost_center: item.limits_cost_center,
          belegart: belegart
        }
      };

      const response = await fetch(`/api/favorites/${editingFavorite.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        showSuccess?.(t('banf.favoriteUpdated'));
        // Call the callback to notify parent component
        if (onFavoriteUpdated) {
          onFavoriteUpdated();
        }
      } else {
        const errorData = await response.json();
        console.error('Fehler beim Aktualisieren des Favoriten:', errorData);

        if (response.status === 401 || response.status === 403) {
          showError?.(t('banf.sessionExpired', { error: errorData.error || t('banf.sessionExpiredDefault') }));
          localStorage.removeItem('sap_oci_user');
          window.location.reload();
          return;
        }

        showError?.(t('banf.favoriteUpdateError') + (errorData.message || t('banf.unknownError')));
      }
    } catch (error) {
      console.error('Error updating favorite:', error);
      showError?.(t('banf.favoriteUpdateError') + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle expand/collapse for an item
  const toggleItemExpansion = (id) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Clear all positions and create one empty position
  const clearAllPositions = () => {
    const initialItem = {
      id: Date.now(),
      position_number: 1,
      cost_center: user?.costCenter || '110700',
      gl_account: '',
      material: '',
      description: '',
      quantity: 1,
      unit: 'ST', // SAP-Mengeneinheit (T006/MSEHI); 'STK' aus der alten Liste kennt SAP nicht
      delivery_date: getDefaultDeliveryDate(),
      product_group: '',
      plant: user?.plant || '1000',
      storage_location: '',
      preferred_vendor: '',
      fixed_vendor: '',
      price: '',
      price_unit: 1, // PEINH — Preiseinheit (Preis je X Einheiten); EBAN.PEINH
      currency: 'EUR',
      // Limits fields
      total_limit: '',
      expected_value: '',
      limits_gl_account: '',
      limits_cost_center: '',
      // Leistungen fields - array of services
      services: [],
      // Texte fields
      position_text: '',
      // Anhänge fields - array of attachments
      attachments: [],
      // Additional fields
      anforderer: user?.requestor || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      einkauefergruppe: user?.purchasingGroup || '',
      einkaueferorganisation: user?.purchasingOrg || '',
      // Kontierung fields
      auftrag: '', // Auftrag field for order-based accounting
      ps_psp_pnr: '', // PSP-Element (KNTTP=P); externer Projektschluessel PS_POSID -> EBKN.PS_PSP_PNR
      // Individual type selection per item
      kontierungstyp: 'kostenstelle', // Default to kostenstelle
      // Bezugsquelle fields
      contract: '',
      info_record: '',
      vendor_material_number: ''
    };
    
    // Clear auto-save timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    setItems([initialItem]);
    setExpandedItems(new Set([initialItem.id]));
    setActiveItemTabs({ [initialItem.id]: 'allgemein' });

    // Entwurf aus der Datenbank löschen
    if (currentDraftId) {
      console.log('📝 Deleting draft on clear all:', currentDraftId);
      deleteDraft(currentDraftId);
      setCurrentDraftId(null);
      // Notify parent to refresh drafts count in header badge
      if (onDraftsChange) {
        onDraftsChange();
      }
    }
    showSuccess?.(t('banf.allItemsRemoved'));
  };

  // Load saved data or create initial item - only run once on mount
  useEffect(() => {
    // If we have OCI items, convert them to CreateBanfView format
    if (ociItems && ociItems.length > 0) {
      console.log('Processing', ociItems.length, 'OCI items for CreateBanfView');
      console.log('OCI Items received:', ociItems);
      
      const convertedItems = ociItems.map((ociItem, index) => {
        console.log(`Converting OCI item ${index + 1}:`, ociItem);
        return {
        id: Date.now() + index, // Unique ID for each item
        position_number: index + 1,
        // Map OCI item fields to CreateBanfView fields
        cost_center: user?.costCenter || '110700',
        gl_account: '', // Sachkonto muss manuell gesetzt werden
        material: '', // OCI-Punch-out liefert keine eigene SAP-Materialnr. — bleibt leer; Lieferantennr. steht in vendor_material_number
        image_url: ociItem.image_url || '', // Produktbild aus OCI NEW_ITEM-ATTACHMENT (falls Bild)
        description: ociItem.description || '', // Kurztext: Artikelname/Beschreibung aus dem OCI Shop
        quantity: ociItem.quantity || 1,
        unit: ociItem.unit === 'PCE' ? 'STK' : (ociItem.unit || 'STK'), // Convert PCE to STK
        delivery_date: getDefaultDeliveryDate(),
        product_group: '',
        plant: user?.plant || '1000',
        storage_location: '',
        preferred_vendor: ociItem.oci_shop_name || ociItem.vendor || '', // Wunschlieferant: Name des OCI-Katalogs
        fixed_vendor: '', // OCI hat keinen festen Lieferanten
        price: ociItem.price || '',
        price_unit: ociItem.price_unit || 1, // PEINH aus OCI/Katalog, Default 1
        currency: ociItem.currency || 'EUR',
        // Limits fields - empty for OCI items
        total_limit: '',
        expected_value: '',
        limits_gl_account: '',
        limits_cost_center: '',
        // Services - empty for OCI items
        services: [],
        // Texts
        position_text: '',
        // Attachments - empty for OCI items
        attachments: [],
        // Additional fields
        anforderer: user?.requestor || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        einkauefergruppe: user?.purchasingGroup || '',
        einkaueferorganisation: user?.purchasingOrg || '',
        auftrag: '',
        kontierungstyp: 'kostenstelle', // Default to kostenstelle
        contract: '',
        info_record: '',
        vendor_material_number: ociItem.vendormat || '',
        belegart: 'NB',
        requesting_department: '',
        requesting_cost_center: '',
        material_group: ''
        };
      });
      
      console.log('Converted items:', convertedItems);
      setItems(convertedItems);
      // Expand the first item by default
      setExpandedItems(new Set([convertedItems[0].id]));
      // Set default tab for all items
      const tabs = {};
      convertedItems.forEach(item => {
        tabs[item.id] = 'allgemein';
      });
      setActiveItemTabs(tabs);
      setBelegart('NB');
      
      showSuccess?.(t('banf.ociItemsLoaded', { count: ociItems.length }));
      return;
    }
    
    // If we're editing a favorite, use that data
    else if (editingFavorite) {
      const editItem = {
        id: Date.now(),
        position_number: 1,
        // Map favorite fields to CreateBanfView fields
        cost_center: editingFavorite.cost_center || editingFavorite.item_data?.cost_center || user?.costCenter || '110700',
        gl_account: editingFavorite.gl_account || editingFavorite.item_data?.gl_account || '',
        material: editingFavorite.material_number || editingFavorite.item_data?.material || '',
        description: editingFavorite.name || '',
        quantity: editingFavorite.quantity || 1,
        unit: editingFavorite.unit || 'STK',
        delivery_date: editingFavorite.item_data?.delivery_date || getDefaultDeliveryDate(),
        product_group: editingFavorite.product_group || editingFavorite.item_data?.product_group || '',
        plant: editingFavorite.plant || editingFavorite.item_data?.plant || user?.plant || '1000',
        storage_location: editingFavorite.storage_location || editingFavorite.item_data?.storage_location || '',
        preferred_vendor: editingFavorite.preferred_vendor || editingFavorite.supplier || '',
        price: editingFavorite.price || '',
        currency: editingFavorite.currency || 'EUR',
        // Limits fields
        total_limit: editingFavorite.item_data?.total_limit || '',
        expected_value: editingFavorite.item_data?.expected_value || '',
        limits_gl_account: editingFavorite.item_data?.limits_gl_account || '',
        limits_cost_center: editingFavorite.item_data?.limits_cost_center || '',
        // Services - parse from item_data if available
        services: (() => {
          const services = editingFavorite.item_data?.services;
          if (!services) return [];
          if (Array.isArray(services)) return services;
          try {
            { const parsed = JSON.parse(services); return Array.isArray(parsed) ? parsed : []; }
          } catch {
            return [];
          }
        })(),
        // Texts
        position_text: editingFavorite.description || editingFavorite.item_data?.position_text || '',
        // Attachments - parse from item_data if available
        attachments: (() => {
          const attachments = editingFavorite.item_data?.attachments;
          if (!attachments) return [];
          if (Array.isArray(attachments)) return attachments;
          try {
            { const parsed = JSON.parse(attachments); return Array.isArray(parsed) ? parsed : []; }
          } catch {
            return [];
          }
        })(),
        // Additional fields
        anforderer: editingFavorite.anforderer || editingFavorite.item_data?.anforderer || user?.requestor || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        einkauefergruppe: editingFavorite.einkauefergruppe || editingFavorite.item_data?.einkauefergruppe || user?.purchasingGroup || '',
        einkaueferorganisation: editingFavorite.einkaueferorganisation || editingFavorite.item_data?.einkaueferorganisation || user?.purchasingOrg || '',
        auftrag: editingFavorite.order_number || editingFavorite.item_data?.auftrag || '',
        ps_psp_pnr: editingFavorite.ps_psp_pnr || editingFavorite.item_data?.ps_psp_pnr || '',
        kontierungstyp: normalizeKontierungstyp(editingFavorite.kontierungstyp || editingFavorite.item_data?.kontierungstyp),
        contract: editingFavorite.item_data?.contract || '',
        info_record: editingFavorite.item_data?.info_record || '',
        vendor_material_number: editingFavorite.item_data?.vendor_material_number || '',
        belegart: editingFavorite.item_data?.belegart || 'NB',
        fixed_vendor: editingFavorite.item_data?.fixed_vendor || ''
      };

      setItems([editItem]);
      setExpandedItems(new Set([editItem.id]));
      setActiveItemTabs({ [editItem.id]: 'allgemein' });
      setBelegart(editingFavorite.item_data?.belegart || 'NB');
      return;
    }

    // If we're editing a cart item, use that data
    else if (editingCartItem) {
      const editItem = {
        id: Date.now(),
        position_number: 1,
        // Map cart item fields to CreateBanfView fields
        cost_center: editingCartItem.cost_center || editingCartItem.kostenstelle || user?.costCenter || '110700',
        gl_account: editingCartItem.gl_account || editingCartItem.sachkonto || '',
        material: editingCartItem.material || editingCartItem.matnr || '',
        description: editingCartItem.description || '',
        quantity: editingCartItem.quantity || 1,
        unit: editingCartItem.unit || 'STK',
        delivery_date: editingCartItem.delivery_date || getDefaultDeliveryDate(),
        product_group: editingCartItem.product_group || '',
        plant: editingCartItem.plant || user?.plant || '1000',
        storage_location: editingCartItem.storage_location || '',
        preferred_vendor: editingCartItem.preferred_vendor || editingCartItem.vendor || '',
        fixed_vendor: editingCartItem.fixed_vendor || '',
        price: editingCartItem.price || '',
        currency: editingCartItem.currency || 'EUR',
        // Limits fields
        total_limit: editingCartItem.total_limit || '',
        expected_value: editingCartItem.expected_value || '',
        limits_gl_account: editingCartItem.limits_gl_account || '',
        limits_cost_center: editingCartItem.limits_cost_center || '',
        // Services - parse JSON string if needed
        services: (() => {
          if (!editingCartItem.services) return [];
          if (Array.isArray(editingCartItem.services)) return editingCartItem.services;
          try {
            { const parsed = JSON.parse(editingCartItem.services); return Array.isArray(parsed) ? parsed : []; }
          } catch {
            console.warn('Failed to parse services JSON, using empty array');
            return [];
          }
        })(),
        // Texts
        position_text: editingCartItem.position_text || editingCartItem.longtext || '',
        // Attachments - parse JSON string if needed
        attachments: (() => {
          if (!editingCartItem.attachments) return [];
          if (Array.isArray(editingCartItem.attachments)) return editingCartItem.attachments;
          try {
            { const parsed = JSON.parse(editingCartItem.attachments); return Array.isArray(parsed) ? parsed : []; }
          } catch {
            console.warn('Failed to parse attachments JSON, using empty array');
            return [];
          }
        })(),
        // Additional fields
        anforderer: editingCartItem.anforderer || user?.requestor || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        einkauefergruppe: editingCartItem.einkauefergruppe || user?.purchasingGroup || '',
        einkaueferorganisation: editingCartItem.einkaueferorganisation || user?.purchasingOrg || '',
        auftrag: editingCartItem.auftrag || '',
        ps_psp_pnr: editingCartItem.ps_psp_pnr || '',
        kontierungstyp: normalizeKontierungstyp(editingCartItem.kontierungstyp),
        contract: editingCartItem.contract || '',
        info_record: editingCartItem.info_record || '',
        vendor_material_number: editingCartItem.vendor_material_number || editingCartItem.vendormat || '',
        belegart: editingCartItem.belegart || 'NB',
        requesting_department: editingCartItem.requesting_department || '',
        requesting_cost_center: editingCartItem.requesting_cost_center || '',
        material_group: editingCartItem.material_group || ''
      };
      
      setItems([editItem]);
      setExpandedItems(new Set([editItem.id]));
      setActiveItemTabs({ [editItem.id]: 'allgemein' });
      setBelegart(editingCartItem.belegart || 'NB');
      return;
    }

    // If we're editing a draft, load that data
    else if (editingDraft) {
      setCurrentDraftId(editingDraft.id);
      const draftItems = editingDraft.items || [];

      if (draftItems.length > 0) {
        // Assign new IDs to items to avoid conflicts
        const loadedItems = draftItems.map((item, index) => ({
          ...item,
          id: Date.now() + index,
          position_number: index + 1,
          // Entwürfe sind der wahrscheinlichste Speicher für Altwerte ('dienstleistung')
          kontierungstyp: normalizeKontierungstyp(item.kontierungstyp)
        }));

        setItems(loadedItems);
        setExpandedItems(new Set([loadedItems[0].id]));

        const tabs = {};
        loadedItems.forEach(item => {
          tabs[item.id] = 'allgemein';
        });
        setActiveItemTabs(tabs);
        setBelegart(editingDraft.belegart || 'NB');
      }

      // Mark as initialized after a short delay to enable auto-save for draft editing
      setTimeout(() => {
        isInitializedRef.current = true;
        console.log('📝 CreateBanfView initialized (editing draft) - auto-save enabled');
      }, 1000);
      return;
    }

    // Create initial empty item (no auto-loading from localStorage anymore)
    const savedData = null;
    
    if (savedData && savedData.items.length > 0) {
      // Restore saved data
      setItems(savedData.items);
      setExpandedItems(savedData.expandedItems);
      setActiveItemTabs(savedData.activeItemTabs);
      setBelegart(savedData.belegart || 'NB');
      showSuccess?.(t('banf.draftRestored'));
    } else {
      // Create initial item if no saved data
      const initialItem = {
        id: Date.now(),
        position_number: 1,
        cost_center: user?.costCenter || '110700',
        gl_account: '',
        material: '',
        description: '',
        quantity: 1,
        unit: 'ST', // SAP-Mengeneinheit (T006/MSEHI); 'STK' aus der alten Liste kennt SAP nicht
        delivery_date: getDefaultDeliveryDate(),
        product_group: '',
        plant: user?.plant || '1000',
        storage_location: '',
        preferred_vendor: '',
        price: '',
        price_unit: 1, // PEINH — Preiseinheit (Preis je X Einheiten); EBAN.PEINH
        currency: 'EUR',
        // Limits fields
        total_limit: '',
        expected_value: '',
        limits_gl_account: '',
        limits_cost_center: '',
        // Leistungen fields - array of services
        services: [],
        // Texte fields
        position_text: '',
        // Anhänge fields - array of attachments
        attachments: [],
        // Additional fields
        anforderer: user?.requestor || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        einkauefergruppe: user?.purchasingGroup || '',
        einkaueferorganisation: user?.purchasingOrg || '',
        // Individual type selection per item
        kontierungstyp: 'kostenstelle' // Default to kostenstelle
      };
      setItems([initialItem]);
      setExpandedItems(new Set([initialItem.id])); // Expand the first item by default
      setActiveItemTabs({ [initialItem.id]: 'allgemein' }); // Set default tab
    }

    // Mark as initialized after a short delay to prevent immediate auto-save
    setTimeout(() => {
      isInitializedRef.current = true;
      console.log('📝 CreateBanfView initialized - auto-save enabled');
    }, 1000);
  }, []); // Empty dependency array - only run once on mount

  // Anwendbare PR-Freigabestrategie aus den geladenen Strategien bestimmen
  const activeStrategy = useMemo(() => {
    if (!Array.isArray(releaseStrategies) || releaseStrategies.length === 0) return null;
    const total = typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0;
    return releaseStrategies.find(s =>
      s.release_group === 'PR' &&
      s.active &&
      total >= Number(s.value_from) &&
      (s.value_to === null || s.value_to === undefined || total < Number(s.value_to))
    ) || null;
  }, [releaseStrategies, totalAmount]);

  return (
    <div className={`${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black'
      : 'bg-white'
    }`}>
      <BanfHeader user={user} onBack={handleBack} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Liquid Glass Artikel-Liste */}
        <>
          <div className="space-y-4">
            {items.map((item, index) => (
              <BanfItemCard
                key={item.id}
                item={item}
                onRemove={removeItem}
                onCopy={copyItem}
                onAddToFavorite={handleAddItemToFavorites}
                onUpdate={updateItem}
                canRemove={items.length > 1}
                selectedType={item.kontierungstyp}
                onTypeChange={(newType) => updateItem(item.id, 'kontierungstyp', newType)}
                costCenters={costCenters}
                units={units}
                productGroups={productGroups}
                plants={plants}
                isExpanded={expandedItems.has(item.id)}
                onToggleExpansion={toggleItemExpansion}
                activeTab={activeItemTabs[item.id] || 'allgemein'}
                onTabChange={(tabName) => setActiveItemTabs(prev => ({ ...prev, [item.id]: tabName }))}
                costCenterName={fieldMappings.cost_center[item.cost_center] || null}
                plantName={fieldMappings.plant[item.plant] || null}
                purchasingOrgName={fieldMappings.purchasing_org[item.einkaueferorganisation] || null}
                purchasingGroupName={fieldMappings.purchasing_group[item.einkauefergruppe] || null}
                user={user}
                validationErrors={validationErrors[item.id] || null}
              />
            ))}
          </div>

          {/* ===== Genehmigungsvorschau ===== */}
          {activeStrategy && Array.isArray(activeStrategy.steps) && activeStrategy.steps.length > 0 && (
            <div className={`mt-6 rounded-2xl overflow-hidden ${
              isDarkMode
                ? 'bg-gray-800/40 border border-white/10 backdrop-blur-sm'
                : 'bg-slate-50 border border-slate-200'
            }`}>
              {/* Header */}
              <div className={`flex items-center justify-between px-5 py-3 ${
                isDarkMode ? 'border-b border-white/10' : 'border-b border-slate-200'
              }`}>
                <div className="flex items-center gap-2">
                  <ShieldCheck className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    {t('banf.release.title')}
                  </span>
                </div>
              </div>

              {/* Strategie-Info + Stufen */}
              <div className="px-5 py-4 space-y-3">
                {/* Strategie-Zeile */}
                <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-sm ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  <span>
                    <span className="font-medium">{t('banf.release.strategy')}:</span>{' '}
                    <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                      {activeStrategy.strategy_code}
                    </span>
                    {activeStrategy.description ? (
                      <span className="ml-1">— {activeStrategy.description}</span>
                    ) : null}
                  </span>
                  <span>
                    <span className="font-medium">{t('banf.release.threshold')}:</span>{' '}
                    {formatNumberDE(activeStrategy.value_from)}{' '}
                    {activeStrategy.value_to !== null && activeStrategy.value_to !== undefined
                      ? `– ${formatNumberDE(activeStrategy.value_to)}`
                      : `+`
                    }{' '}
                    {activeStrategy.currency || 'EUR'}
                  </span>
                </div>

                {/* Freigabe-Stufen */}
                <ol className="space-y-1.5">
                  {activeStrategy.steps.map((step) => (
                    <li
                      key={step.seq}
                      className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm ${
                        isDarkMode
                          ? 'bg-white/5 border border-white/10'
                          : 'bg-white border border-slate-200'
                      }`}
                    >
                      {/* Schritt-Nummer */}
                      <span className={`flex-none w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        isDarkMode
                          ? 'bg-blue-500/30 text-blue-300 border border-blue-400/40'
                          : 'bg-blue-100 text-blue-700 border border-blue-200'
                      }`}>
                        {step.seq}
                      </span>

                      {/* Code + Label */}
                      <span className={`flex-1 font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        {step.release_code}
                        {step.code_label ? (
                          <span className={`ml-1 font-normal ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            — {step.code_label}
                          </span>
                        ) : null}
                      </span>

                      {/* Status-Badge */}
                      <span className={`flex-none text-xs px-2 py-0.5 rounded-full ${
                        isDarkMode
                          ? 'bg-slate-700/60 text-slate-400 border border-white/10'
                          : 'bg-slate-100 text-slate-500 border border-slate-200'
                      }`}>
                        {t('banf.release.pending')}
                      </span>
                    </li>
                  ))}
                </ol>

              </div>
            </div>
          )}

          <BanfActionButtons
            onAddItem={editingCartItem || editingFavorite || ociItems ? null : addItem} // Disable adding items in edit mode or OCI mode
            onClearAll={editingCartItem || editingFavorite || ociItems ? null : clearAllPositions} // Disable clearing in edit mode or OCI mode
            totalAmount={totalAmount}
            onSave={editingCartItem ? updateCartItem : (editingFavorite ? updateFavorite : addBanfToCart)} // Use update function in edit mode
            saving={saving}
            hasItems={items.length > 0}
            itemCount={items.length}
            belegart={belegart}
            onBelegartChange={setBelegart}
            editMode={!!editingCartItem || !!editingFavorite} // Pass edit mode flag (works for both cart and favorites)
            ociMode={!!ociItems} // Pass OCI mode flag
          />
        </>
      </div>
    </div>
  );
};

export default CreateBanfView;